import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, googleLoginUser, clearSignUpClosed } from './accountSlice';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { GOOGLE_CLIENT_ID } from '../../config/auth';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import DWLFLoader from '../../components/DWLFLoader';
import { emailFromGoogleCredential } from '../waitlist/googleEmail';
import { WAITLIST_COPY } from '../waitlist/waitlistCopy';
import WaitlistForm from '../waitlist/WaitlistForm';

// ============================================================================
// DWLF Login — Brutalist Design
// ============================================================================

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Captured once on mount: add-account mode only applies when we're ACTUALLY
  // signed in already. Hitting /login?addAccount=1 while logged out is just a
  // normal login (and the "you're signed in as…" banner can't mislead).
  const [addAccountMode] = useState(
    () => searchParams.get('addAccount') === '1' && Boolean(localStorage.getItem('authToken')),
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');
  const { token, user, loginStatus, error, signUpClosed } = useSelector((state) => state.account);

  useEffect(() => {
    dispatch(clearSignUpClosed());
  }, [dispatch]);

  // Normal login: redirect once a token exists. In add-account mode we're
  // already authenticated as another account, so suppress that redirect and
  // let the user sign in as a second account instead.
  useEffect(() => {
    if (token && !addAccountMode) {
      navigate('/today');
    }
  }, [token, navigate, addAccountMode]);

  // Add-account mode: once THIS sign-in succeeds, hard-reload as the new account
  // for a clean bootstrap. The previously-active account stays in the registry.
  useEffect(() => {
    if (addAccountMode && submitted && loginStatus === 'succeeded') {
      window.location.assign('/today');
    }
  }, [addAccountMode, submitted, loginStatus]);

  const handleLogin = (e) => {
    e.preventDefault();
    setSubmitted(true);
    dispatch(loginUser({ email, password }));
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setSubmitted(true);
    setGoogleEmail(emailFromGoogleCredential(credentialResponse.credential));
    dispatch(googleLoginUser(credentialResponse.credential));
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'var(--dwlf-bg)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          position: 'relative',
        }}
      >
        {/* Subtle noise texture */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.025,
            pointerEvents: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            zIndex: 0,
          }}
        />

        {/* Logo */}
        <Typography
          component={Link}
          to="/"
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontSize: '2rem',
            fontWeight: 600,
            color: 'var(--dwlf-accent)',
            textDecoration: 'none',
            mb: 6,
            letterSpacing: '-0.02em',
            position: 'relative',
            zIndex: 1,
          }}
        >
          DWLF
        </Typography>

        {/* Login Form */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 380,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontSize: '1.75rem',
              fontWeight: 500,
              color: 'var(--dwlf-text)',
              mb: 1,
              letterSpacing: '-0.02em',
            }}
          >
            Sign in
          </Typography>
          <Typography
            sx={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.85rem',
              color: 'var(--dwlf-ink-50)',
              mb: 4,
            }}
          >
            {addAccountMode ? 'Sign in to add another account' : 'Enter your credentials to continue'}
          </Typography>

          {addAccountMode && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                border: '1px solid var(--dwlf-accent-30)',
                backgroundColor: 'var(--dwlf-accent-06)',
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.8rem',
                  color: 'var(--dwlf-ink-70)',
                }}
              >
                Adding another account{user?.email ? ` — you're signed in as ${user.email}` : ''}.
              </Typography>
              <Typography
                component={Link}
                to="/today"
                sx={{
                  display: 'inline-block',
                  mt: 1,
                  color: 'var(--dwlf-accent)',
                  textDecoration: 'none',
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.8rem',
                }}
              >
                ← Cancel, keep current account
              </Typography>
            </Box>
          )}

          <form onSubmit={handleLogin}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              required
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: {
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.85rem',
                }
              }}
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              sx={{ mb: 3 }}
              InputLabelProps={{
                sx: {
                  fontFamily: '"IBM Plex Mono", monospace',
                  fontSize: '0.85rem',
                }
              }}
            />
            <Button
              type="submit"
              fullWidth
              disabled={loginStatus === 'loading'}
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.8rem',
                fontWeight: 500,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--dwlf-bg)',
                backgroundColor: 'var(--dwlf-accent)',
                borderRadius: 0,
                py: 1.5,
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: 'var(--dwlf-text)',
                },
                '&:disabled': {
                  backgroundColor: 'var(--dwlf-accent-50)',
                  color: 'var(--dwlf-bg)',
                },
              }}
            >
              {loginStatus === 'loading' ? (
                <DWLFLoader size="tiny" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {signUpClosed && <SignUpClosed email={googleEmail} />}

          {error && !signUpClosed && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 3,
                borderRadius: 0,
                backgroundColor: 'var(--dwlf-down-10)',
                border: '1px solid var(--dwlf-down)',
                '& .MuiAlert-icon': {
                  color: 'var(--dwlf-down)',
                },
              }}
            >
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              my: 4,
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'var(--dwlf-ink-10)' }} />
            <Typography
              sx={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '0.75rem',
                color: 'var(--dwlf-ink-40)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              or
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'var(--dwlf-ink-10)' }} />
          </Box>

          {/* Google Sign-In */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <GoogleLogin 
              onSuccess={handleGoogleSuccess}
              onError={() => console.error("Google login failed")}
              theme="filled_black"
              shape="rectangular"
            />
          </Box>

          {/* Sign Up Link */}
          <Typography
            sx={{
              mt: 4,
              textAlign: 'center',
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '0.85rem',
              color: 'var(--dwlf-ink-60)',
            }}
          >
            Don't have an account?{' '}
            <Link
              to="/waitlist?from=sign_in"
              style={{
                color: 'var(--dwlf-accent)',
                textDecoration: 'none',
              }}
            >
              {WAITLIST_COPY.cta}
            </Link>
          </Typography>
        </Box>

        {/* Footer */}
        <Typography
          sx={{
            position: 'absolute',
            bottom: 32,
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.7rem',
            color: 'var(--dwlf-ink-30)',
            letterSpacing: '0.1em',
          }}
        >
          SIGNAL FROM NOISE
        </Typography>
      </Box>
    </GoogleOAuthProvider>
  );
};

/**
 * A Google account with no DWLF account, while sign-up is closed: the waitlist, not an error
 * (DWLF-584). The form is shown in place so the Google address never travels in a URL.
 */
function SignUpClosed({ email }) {
  return (
    <Box sx={{ mt: 3, p: 2, border: '1px solid var(--dwlf-ink-20)', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Only the sentence is announced; the form is ordinary content the person moves through. */}
      <Typography role="alert" sx={{ color: 'var(--dwlf-text)', fontSize: '0.95rem' }}>
        {WAITLIST_COPY.refusedLegacy} {WAITLIST_COPY.intro}
      </Typography>
      <WaitlistForm initialEmail={email} place="refused" />
    </Box>
  );
}

export default Login;
